import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, setSessionEndCallback } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, api } = useAuth();
  
  const [session, setSession] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);
  const [lastScannedItem, setLastScannedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeoutAt, setTimeoutAt] = useState(null);
  
  const prevItemsRef = useRef([]);
  const pollIntervalRef = useRef(null);
  const currentSessionIdRef = useRef(null);
  const isEndingRef = useRef(false);

  // Clear all cart state
  const clearState = useCallback(() => {
    currentSessionIdRef.current = null;
    setSession(null);
    setCartItems([]);
    setSubtotal(0);
    setTax(0);
    setTotal(0);
    setLastScannedItem(null);
    setTimeoutAt(null);
    prevItemsRef.current = [];
    
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // End session - MUST release cart
  const endSession = useCallback(async () => {
    // Get session ID from ref (most reliable) or state
    const sessionId = currentSessionIdRef.current || session?.id;
    
    console.log('[CartContext] endSession called');
    console.log('[CartContext] - currentSessionIdRef:', currentSessionIdRef.current);
    console.log('[CartContext] - session?.id:', session?.id);
    console.log('[CartContext] - isEnding:', isEndingRef.current);
    
    if (!sessionId) {
      console.warn('[CartContext] No session ID available, clearing state only');
      clearState();
      return;
    }
    
    if (isEndingRef.current) {
      console.warn('[CartContext] Already ending session, skipping duplicate call');
      return;
    }
    
    isEndingRef.current = true;
    
    // Stop polling immediately to prevent race conditions
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    let apiSuccess = false;
    const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/sessions/${sessionId}/end`;
    
    try {
      console.log('[CartContext] Calling POST', apiUrl);
      const response = await api.post(`/sessions/${sessionId}/end`);
      console.log('[CartContext] Session ended successfully:', response.data);
      apiSuccess = true;
    } catch (e) { 
      console.error('[CartContext] End session API error:', e.response?.status, e.response?.data || e.message);
      
      // If session not found (404) or already ended (400), still clear state
      if (e.response?.status === 404 || e.response?.status === 400) {
        console.log('[CartContext] Session already ended or not found, clearing state');
        apiSuccess = true; // Consider it success since session is already gone
      } else {
        // Try direct fetch as fallback (in case axios has auth issues)
        try {
          console.log('[CartContext] Trying direct fetch fallback to:', apiUrl);
          const token = localStorage.getItem('smartcaddy_token');
          const fetchResponse = await fetch(apiUrl, { 
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          if (fetchResponse.ok) {
            console.log('[CartContext] Session ended via direct fetch');
            apiSuccess = true;
          } else {
            const errorData = await fetchResponse.json().catch(() => ({}));
            console.error('[CartContext] Direct fetch failed:', fetchResponse.status, errorData);
          }
        } catch (fetchErr) {
          console.error('[CartContext] Direct fetch also failed:', fetchErr);
        }
        
        // Last resort: beacon (doesn't require auth)
        if (!apiSuccess) {
          try {
            console.log('[CartContext] Trying beacon endpoint as last resort');
            const beaconResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/sessions/${sessionId}/beacon-end`, 
              { method: 'POST' }
            );
            if (beaconResponse.ok) {
              console.log('[CartContext] Session ended via beacon');
              apiSuccess = true;
            }
          } catch (beaconErr) {
            console.error('[CartContext] Beacon also failed:', beaconErr);
          }
        }
      }
    }
    
    // Always clear state regardless of API result
    clearState();
    isEndingRef.current = false;
    
    if (!apiSuccess) {
      throw new Error('Failed to end session on server');
    }
  }, [session, api, clearState]);

  useEffect(() => {
    setSessionEndCallback(endSession);
    return () => setSessionEndCallback(null);
  }, [endSession]);

  // Browser close - beacon
  // Note: We DON'T end session on beforeunload because it fires on refresh too
  // Instead, we rely on server-side session timeout (15 min) to cleanup abandoned sessions
  // The session persists and can be resumed if user refreshes or navigates away and back
  useEffect(() => {
    // Use sessionStorage to track if this is a refresh vs new session
    const handleBeforeUnload = () => {
      // Set a flag that we're reloading - this persists across refresh
      sessionStorage.setItem('smartcaddy_refreshing', 'true');
    };
    
    const handleLoad = () => {
      // Check if we're resuming from a refresh
      const wasRefreshing = sessionStorage.getItem('smartcaddy_refreshing');
      if (wasRefreshing) {
        sessionStorage.removeItem('smartcaddy_refreshing');
        console.log('[CartContext] Page was refreshed, session will be resumed');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('load', handleLoad);
    
    // Only send beacon on actual browser close (detected via pagehide with persisted=false)
    const handlePageHide = (event) => {
      // event.persisted is true for bfcache (back/forward), false for actual close
      if (!event.persisted) {
        const sid = currentSessionIdRef.current;
        const wasRefreshing = sessionStorage.getItem('smartcaddy_refreshing');
        // Only end session if not a refresh
        if (sid && !wasRefreshing) {
          console.log('[CartContext] Browser closing, ending session:', sid);
          navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/sessions/${sid}/beacon-end`);
        }
      }
    };
    
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Load session
  const loadSession = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const { data } = await api.get('/sessions/active');
      currentSessionIdRef.current = data.id;
      setSession(data);
      const items = data.cart_items || [];
      setCartItems(items);
      prevItemsRef.current = items;
      setSubtotal(data.subtotal || 0);
      setTax(data.tax || 0);
      setTotal(data.total || 0);
      setTimeoutAt(data.timeout_at);
    } catch {
      currentSessionIdRef.current = null;
      setSession(null);
      setCartItems([]);
      prevItemsRef.current = [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, api]);

  useEffect(() => {
    if (isAuthenticated) loadSession();
    else { 
      clearState();
      setLoading(false); 
    }
  }, [isAuthenticated, loadSession, clearState]);

  // Detect item changes and set lastScannedItem
  const detectItemChange = useCallback((oldItems, newItems) => {
    if (!newItems?.length) return;
    
    // Find items that changed
    for (const newItem of newItems) {
      const oldItem = oldItems.find(i => i.product_id === newItem.product_id);
      
      if (!oldItem) {
        // New item added
        setLastScannedItem({ ...newItem, action: 'ADD', timestamp: Date.now() });
        setTimeout(() => setLastScannedItem(null), 5000);
        return;
      }
      
      if (newItem.quantity !== oldItem.quantity) {
        const action = newItem.quantity > oldItem.quantity ? 'ADD' : 'REMOVE';
        setLastScannedItem({ ...newItem, action, timestamp: Date.now() });
        setTimeout(() => setLastScannedItem(null), 5000);
        return;
      }
    }
    
    // Check for removed items
    for (const oldItem of oldItems) {
      const stillExists = newItems.find(i => i.product_id === oldItem.product_id);
      if (!stillExists) {
        setLastScannedItem({ ...oldItem, quantity: 0, action: 'REMOVE', timestamp: Date.now() });
        setTimeout(() => setLastScannedItem(null), 5000);
        return;
      }
    }
  }, []);

  // Polling - CRITICAL: Use currentSessionIdRef to ensure correct session
  useEffect(() => {
    if (!session || !isAuthenticated || isEndingRef.current) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const sessionIdToTrack = session.id;
    currentSessionIdRef.current = sessionIdToTrack;

    const poll = async () => {
      // Don't poll if ending session or session changed
      if (isEndingRef.current || currentSessionIdRef.current !== sessionIdToTrack) return;
      
      try {
        const { data } = await api.get('/sessions/active');
        
        // Verify this is the session we're tracking
        if (data.id !== sessionIdToTrack) {
          console.warn('[CartContext] Session mismatch, reloading...');
          loadSession();
          return;
        }

        if (!data || data.status !== 'active') {
          clearState();
          return;
        }

        setTimeoutAt(data.timeout_at);

        const newItems = data.cart_items || [];
        const oldItems = prevItemsRef.current;
        
        // Check if items actually changed (deep compare)
        const itemsChanged = JSON.stringify(newItems) !== JSON.stringify(oldItems);
        
        if (itemsChanged) {
          detectItemChange(oldItems, newItems);
          prevItemsRef.current = newItems;
          setCartItems(newItems);
          setSubtotal(data.subtotal || 0);
          setTax(data.tax || 0);
          setTotal(data.total || 0);
        }
      } catch (e) {
        if (e.response?.status === 404) {
          clearState();
        }
      }
    };

    pollIntervalRef.current = setInterval(poll, 1500);
    poll();
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [session?.id, isAuthenticated, api, loadSession, clearState, detectItemChange]);

  const validateCart = async (cartCode) => {
    const { data } = await api.post('/carts/validate', { cart_code: cartCode });
    return data;
  };

  const createSession = async (cartId) => {
    // End any existing session first
    if (currentSessionIdRef.current) {
      try { await endSession(); } catch (err) { console.warn('[CartContext] Failed to end previous session:', err); }
    }
    
    const { data } = await api.post('/sessions', { cart_id: cartId });
    currentSessionIdRef.current = data.id;
    setSession(data);
    const items = data.cart_items || [];
    setCartItems(items);
    prevItemsRef.current = items;
    setSubtotal(data.subtotal || 0);
    setTax(data.tax || 0);
    setTotal(data.total || 0);
    setTimeoutAt(data.timeout_at);
    return data;
  };

  const checkout = async () => {
    if (!session) throw new Error('No active session');
    
    console.log('[CartContext] Starting checkout for session:', session.id);
    
    // Stop polling during checkout
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    try {
      const { data } = await api.post('/checkout/initiate', { session_id: session.id });
      console.log('[CartContext] Checkout completed:', data);
      
      // Clear state after checkout (cart is released by backend)
      clearState();
      
      return data;
    } catch (e) {
      // Restart polling on error
      console.error('[CartContext] Checkout error:', e);
      throw e;
    }
  };

  const createPaymentOrder = async () => {
    if (!session) throw new Error('No active session');
    
    console.log('[CartContext] Creating payment order for session:', session.id);
    
    const { data } = await api.post('/checkout/create-order', { session_id: session.id });
    return data;
  };

  const verifyPayment = async (razorpayOrderId, razorpayPaymentId, razorpaySignature) => {
    if (!session) throw new Error('No active session');
    
    console.log('[CartContext] Verifying payment for session:', session.id);
    
    // Stop polling during verification
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    try {
      const { data } = await api.post('/checkout/verify-payment', {
        session_id: session.id,
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: razorpayPaymentId,
        razorpay_signature: razorpaySignature
      });
      
      console.log('[CartContext] Payment verified:', data);
      
      // Clear state after successful payment
      clearState();
      
      return data;
    } catch (e) {
      console.error('[CartContext] Payment verification error:', e);
      throw e;
    }
  };

  const extendSession = async () => {
    if (!session) return;
    try {
      const { data } = await api.post(`/sessions/${session.id}/extend`);
      setTimeoutAt(data.timeout_at);
    } catch (e) { console.error('Extend error:', e); }
  };

  return (
    <CartContext.Provider value={{
      session, cartItems, subtotal, tax, total, lastScannedItem, loading, timeoutAt,
      hasActiveSession: !!session,
      itemCount: cartItems.reduce((s, i) => s + i.quantity, 0),
      validateCart, createSession, endSession, checkout, extendSession, refreshSession: loadSession,
      createPaymentOrder, verifyPayment,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
